import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { formatArea } from '.';
import { GEOMETRY_TYPES, UNITS } from '../constants';
import type { Graphic } from '../types';

/**
 * Calculates and sets the area for a polygon graphic.
 *
 * Uses ArcGIS geodetic area calculation (via geometryEngine) for accurate results
 * on geographic coordinates. Falls back to planar area calculation if geodetic fails.
 * Sets the formatted area string in the graphic's attributes for display in the UI.
 *
 * @param graphic - The graphic object containing polygon geometry and attributes
 *
 * @remarks
 * - Only processes graphics with polygon geometry type
 * - Sets `graphic.attributes.area` to formatted string (e.g., "150 m² / 1,615 ft²")
 * - Sets area to `undefined` for non-polygon geometries or calculation failures
 * - Uses geodetic calculations for geographic accuracy
 * - Automatically formats area in both metric and imperial units
 *
 * @example
 * const polygonGraphic = new Graphic({
 *   geometry: polygonGeometry,
 *   attributes: { note: 'My polygon' }
 * });
 *
 * calculateAndSetArea(polygonGraphic) - "1,500 m² / 16,146 ft²"
 */
export const calculateAndSetArea = (graphic: Graphic): void => {
  if (graphic.geometry?.type === GEOMETRY_TYPES.POLYGON) {
    try {
      const areaInSquareMeters = Math.abs(
        geometryEngine.geodesicArea(
          graphic.geometry as Polygon,
          UNITS.SQUARE_METERS,
        ),
      );
      graphic.attributes.area = formatArea(areaInSquareMeters);
    } catch (error) {
      try {
        const areaInSquareMeters = Math.abs(
          geometryEngine.planarArea(
            graphic.geometry as Polygon,
            UNITS.SQUARE_METERS,
          ),
        );
        graphic.attributes.area = formatArea(areaInSquareMeters);
      } catch (fallbackError) {
        graphic.attributes.area = '';
      }
    }
  } else {
    graphic.attributes.area = '';
  }
};
